import api from './api';

export interface BusinessInfoPayload {
    name?: string;
    tax_code?: string;
    address?: string;
    phone?: string;
    email?: string;
    logo?: string;
    city_id?: string;
    ward_id?: string;
    representative?: string;
    business_type?: string;
    founded_year?: string;
    website?: string;
}

export interface BusinessInfo extends BusinessInfoPayload {
    /** snake_case từ BE — match TenantSettingsExt.business_info. */
}

export interface PaymentShift {
    name: string;
    start_time: string;
    end_time: string;
}

export interface PaymentConfig {
    methods: string[];
    default_method: string;
    shifts: PaymentShift[];
    require_shift_open: boolean;
}

export interface SettingsFull {
    business: BusinessInfo;
    payment: PaymentConfig;
    einvoice: {
        enabled: boolean;
        provider: 'misa' | 'viettel' | 'self';
        auto_issue: boolean;
        default_template?: string | null;
    };
    notifications: {
        email: { enabled: boolean };
        zalo_zns: { enabled: boolean };
        sms: { enabled: boolean };
    };
    integrations: {
        ghtk: { enabled: boolean };
        napas: { enabled: boolean };
    };
    api_keys_enabled: boolean;
}

export interface LocaleSettings {
    language: 'vi' | 'en';
    timezone: string;
    currency: 'VND' | 'USD';
    date_format?: string;
}

export interface LocaleSettingsUpdateInput {
    language?: 'vi' | 'en';
    timezone?: string | null;
    currency?: 'VND' | 'USD';
    date_format?: string;
}


interface ApiEnvelope<T> {
    data: T;
    success: boolean;
}

// ===== Phase 3 — E-Invoice =====
export interface EInvoiceConfig {
    enabled: boolean;
    provider: 'misa' | 'viettel' | 'self';
    auto_issue: boolean;
    default_template?: string | null;
    has_credentials: boolean;
}

export interface EInvoiceUpdateInput {
    enabled?: boolean;
    provider?: 'misa' | 'viettel' | 'self';
    auto_issue?: boolean;
    default_template?: string;
    /** JSON string của credentials. BE sẽ encrypt server-side. */
    credentials_json?: string;
}

// ===== Phase 3 — Notifications =====
export interface NotificationsConfig {
    email: {
        enabled: boolean;
        smtp_host: string | null;
        smtp_port: number | null;
        smtp_user: string | null;
        from_address: string | null;
        has_password: boolean;
    };
    zalo_zns: {
        enabled: boolean;
        oa_id: string | null;
        has_token: boolean;
    };
    sms: {
        enabled: boolean;
        provider: string | null;
        has_api_key: boolean;
    };
}

export interface NotificationsUpdateInput {
    email?: {
        enabled?: boolean;
        smtp_host?: string;
        smtp_port?: number;
        smtp_user?: string;
        smtp_pass?: string;
        from_address?: string;
    };
    zalo_zns?: {
        enabled?: boolean;
        oa_id?: string;
        access_token?: string;
    };
    sms?: {
        enabled?: boolean;
        provider?: string;
        api_key?: string;
    };
}

// ===== Integrations — GHTK + Napas =====
export interface ShippingProviderIntegrationConfig {
    provider: 'ghtk' | 'ahamove' | 'viettelpost' | 'manual';
    label: string;
    enabled: boolean;
    has_token: boolean;
    has_webhook_token: boolean;
}

export interface IntegrationsConfig {
    shipping_providers?: ShippingProviderIntegrationConfig[];
    ghtk: {
        enabled: boolean;
        has_token: boolean;
        has_webhook_token: boolean;
    };
    best?: {
        enabled: boolean;
        active_environment: 'test' | 'production';
        credentials_by_env: Record<'test' | 'production', {
            username: string | null;
            has_password: boolean;
            has_ems_key: boolean;
            has_api_token: boolean;
            api_token_expires_at: string | null;
            has_webhook_bearer_token: boolean;
            verified_at: string | null;
        }>;
        /** Nhịp tự hỏi Best về đơn chưa kết thúc, phút. `null` = tắt. */
        poll_interval_minutes: number | null;
    };
    /** Số ngày giữ nhật ký gọi API vận chuyển — áp cho MỌI hãng. */
    api_log_retention_days?: number;
    /**
     * Hạn mức COD theo mã hãng. `max = 0` = không chặn trên.
     *
     * ĐỌC là map (tra theo mã hãng cho gọn), GHI là mảng — xem
     * `IntegrationsUpdateInput.cod_limits`. Bất đối xứng có chủ đích: ràng buộc
     * nằm ở tầng validate của BE, không phải ở tầng đọc.
     */
    cod_limits?: Record<string, { min: number; max: number }>;
    napas: {
        enabled: boolean;
        client_id: string | null;
        account_prefix: string | null;
        has_public_key: boolean;
        has_private_key: boolean;
    };
}

export interface IntegrationsUpdateInput {
    shipping_providers?: Array<{
        provider: ShippingProviderIntegrationConfig['provider'];
        enabled?: boolean;
        token?: string;
        webhook_token?: string;
    }>;
    ghtk?: {
        enabled?: boolean;
        token?: string;
        webhook_token?: string;
    };
    best?: {
        enabled?: boolean;
        active_environment?: 'test' | 'production';
        username?: string;
        password?: string;
        ems_key?: string;
        webhook_bearer_token?: string;
        poll_interval_minutes?: number | null;
    };
    api_log_retention_days?: number;
    /**
     * Hạn mức COD theo hãng. Dạng MẢNG vì ValidationPipe của BE bật
     * `forbidNonWhitelisted` — object khoá động bị trả 400.
     * Chỉ hãng có trong mảng bị ghi đè.
     */
    cod_limits?: Array<{ carrier: string; min?: number; max?: number }>;
    napas?: {
        enabled?: boolean;
        client_id?: string;
        account_prefix?: string;
        public_key?: string;
        private_key?: string;
    };
}

export interface BestConnectionInput {
    environment: 'test' | 'production';
    username: string;
    password?: string;
    /** Bỏ trống = dùng ems-key đã lưu; '' = thử ở tư cách small shop. */
    ems_key?: string;
}

export interface BestConnectionResult {
    connected: true;
    message: string;
}

export interface BestWebhookTokenInput {
    /** Bỏ trống = dùng active_environment đang cấu hình. */
    environment?: 'test' | 'production';
}

export interface BestWebhookTokenResult {
    /** Plaintext — chỉ dùng để hiển thị cho người dùng chuyển cho nhân viên Best. */
    webhook_bearer_token: string | null;
    environment: 'test' | 'production';
}

// ===== Phase 4 — API Keys =====
export interface ApiKey {
    _id: string;
    tenant_id: string;
    name: string;
    key_prefix: string;
    permissions: string[];
    is_active: boolean;
    last_used_at?: string;
    revoked_at?: string;
    created_by?: string;
    created_at?: string;
}

export interface CreateApiKeyInput {
    name: string;
    permissions?: string[];
}

export interface CreateApiKeyResponse {
    key: string;
    record: ApiKey;
}

// ===== Phase 4 — Audit Log =====
export interface AuditLogEntry {
    _id: string;
    tenant_id: string;
    user_id?: string;
    user_name?: string;
    section: string;
    action: string;
    target_id?: string;
    diff?: Record<string, unknown>;
    ip?: string;
    user_agent?: string;
    created_at: string;
}

export interface AuditLogQueryInput {
    page?: number;
    page_size?: number;
    section?: string;
    user_id?: string;
    date_from?: string;
    date_to?: string;
}

export interface AuditLogResponse {
    data: AuditLogEntry[];
    meta: { total: number; page: number; page_size: number };
}

export interface BankAccount {
    _id: string;
    tenant_id: string;
    bank_name: string;
    bank_code?: string;
    account_number: string;
    account_name: string;
    branch?: string;
    /**
     * TCR-1901 — chi nhánh áp dụng tài khoản này. `null`/thiếu = dùng chung mọi
     * chi nhánh. KHÔNG phải `branch` (chi nhánh của ngân hàng, chuỗi tự do).
     */
    store_id?: string | null;
    enabled?: boolean;
    is_default: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface CreateBankAccountInput {
    bank_name: string;
    bank_code?: string;
    account_number: string;
    account_name: string;
    branch?: string;
    /** `null` = dùng chung mọi chi nhánh (TCR-1901). */
    store_id?: string | null;
    is_default?: boolean;
    enabled?: boolean;
}

export type UpdateBankAccountInput = Partial<
    Omit<CreateBankAccountInput, 'is_default'>
>;

export interface EWallet {
    _id: string;
    tenant_id: string;
    provider: string;
    account_number: string;
    account_name: string;
    enabled?: boolean;
    is_default: boolean;
    created_at?: string;
}
export interface CreateEWalletInput {
    provider: string;
    account_number: string;
    account_name: string;
    is_default?: boolean;
    enabled?: boolean;
}
export type UpdateEWalletInput = Partial<Omit<CreateEWalletInput, 'is_default'>>;

export interface CardTerminal {
    _id: string;
    tenant_id: string;
    acquirer: string;
    terminal_id: string;
    account_number?: string;
    account_name: string;
    enabled?: boolean;
    is_default: boolean;
    created_at?: string;
}
export interface CreateCardTerminalInput {
    acquirer: string;
    terminal_id: string;
    account_number?: string;
    account_name: string;
    is_default?: boolean;
    enabled?: boolean;
}
export type UpdateCardTerminalInput = Partial<
    Omit<CreateCardTerminalInput, 'is_default'>
>;

// ===== Cấu hình chung =====
export type PosPaymentMethod = 'cash' | 'bank' | 'wallet' | 'card';

export interface GeneralConfig {
    serial_tracking_enabled: boolean;
    pos_personal_cccd_required: boolean;
    pos_weight_column_enabled: boolean;
    /** Cho phép nút chuyển sản phẩm thường ↔ combo thật (giữ SKU và tồn). Mặc định tắt. */
    product_combo_conversion_enabled: boolean;
    pos_sales_modes: ('INSTANT' | 'PREORDER')[];
    /** TCR-1428 — hình thức thanh toán cho phép tại POS. Mặc định BE không gồm 'wallet'. */
    pos_payment_methods: PosPaymentMethod[];
    pos_auto_sync_customer_enabled: boolean;
    /** ID dịch vụ (danh mục Dịch vụ) được phép dùng làm phụ thu tại POS. */
    pos_surcharge_service_ids: string[];
    /** VAT mặc định khi thu ngân gõ phụ thu tự do hoặc dịch vụ chưa khai thuế suất. */
    pos_surcharge_default_vat_rate: number;
    /** ID dịch vụ được phép dùng làm phí vận chuyển khi đơn tự vận chuyển. */
    pos_shipping_service_ids: string[];
    /** VAT mặc định cho phí vận chuyển tự vận chuyển. */
    pos_shipping_default_vat_rate: number;
    pos_default_fulfillment_choice: 'pickup' | 'self' | 'carrier';
    /**
     * TCR-1883 — công tắc TỔNG kiểm tra hạn mức công nợ khi lên đơn B2B.
     * Tắt = bỏ qua toàn bộ, kể cả khách đã được đặt hạn mức riêng.
     */
    b2b_credit_check_enabled: boolean;
    /**
     * TCR-2395 — công tắc kiểm tra công nợ riêng cho kênh GT. Chỉ có tác dụng
     * khi công tắc tổng đang bật. Thiếu field = bật (tenant cũ chưa có).
     */
    b2b_credit_check_gt_enabled: boolean;
    /**
     * TCR-2395 — công tắc kiểm tra công nợ riêng cho kênh MT. Chỉ có tác dụng
     * khi công tắc tổng đang bật. Thiếu field = bật (tenant cũ chưa có).
     */
    b2b_credit_check_mt_enabled: boolean;
    /** TCR-1883 — hạn mức áp cho khách chưa thiết lập riêng. 0 = không giới hạn. */
    b2b_credit_default_limit: number;
    /** TCR-1963 — số ngày giữ hàng đơn Nháp trước khi tự huỷ, mặc định 2, 1–30. */
    draft_order_hold_days: number;
}

export interface GeneralUpdateInput {
    serial_tracking_enabled?: boolean;
    pos_personal_cccd_required?: boolean;
    pos_weight_column_enabled?: boolean;
    product_combo_conversion_enabled?: boolean;
    pos_sales_modes?: ('INSTANT' | 'PREORDER')[];
    pos_payment_methods?: PosPaymentMethod[];
    pos_auto_sync_customer_enabled?: boolean;
    pos_surcharge_service_ids?: string[];
    pos_surcharge_default_vat_rate?: number;
    pos_shipping_service_ids?: string[];
    pos_shipping_default_vat_rate?: number;
    pos_default_fulfillment_choice?: 'pickup' | 'self' | 'carrier';
    b2b_credit_check_enabled?: boolean;
    /** TCR-2395 */
    b2b_credit_check_gt_enabled?: boolean;
    /** TCR-2395 */
    b2b_credit_check_mt_enabled?: boolean;
    b2b_credit_default_limit?: number;
    /** TCR-1963 */
    draft_order_hold_days?: number;
}

export interface ProductSearchSyncResult {
    /** Số sản phẩm đã đẩy vào hàng đợi index. */
    queued: number;
}

/** 5 phút: đủ cho cửa hàng vài chục nghìn sản phẩm, vẫn có điểm dừng. */
const PRODUCT_SEARCH_SYNC_TIMEOUT_MS = 5 * 60 * 1000;

export const settingsService = {
    /** GET /v1/settings — composite read. */
    getFull: () =>
        api.get<ApiEnvelope<SettingsFull>>('/settings').then((r) => r.data.data),

    getLocale: () =>
        api.get<ApiEnvelope<LocaleSettings>>('/settings/locale').then((r) => r.data.data),

    updateLocale: (body: LocaleSettingsUpdateInput) =>
        api
            .patch<ApiEnvelope<LocaleSettings>>('/settings/locale', body)
            .then((r) => r.data.data),

    getBusiness: () =>
        api.get<ApiEnvelope<BusinessInfo>>('/settings/business').then((r) => r.data.data),

    updateBusiness: (body: BusinessInfoPayload) =>
        api
            .patch<ApiEnvelope<BusinessInfo>>('/settings/business', body)
            .then((r) => r.data.data),

    getPayment: () =>
        api.get<ApiEnvelope<PaymentConfig>>('/settings/payment').then((r) => r.data.data),

    updatePayment: (body: Partial<PaymentConfig>) =>
        api
            .patch<ApiEnvelope<PaymentConfig>>('/settings/payment', body)
            .then((r) => r.data.data),

    // ===== Phase 2 — Bank accounts =====
    listBankAccounts: () =>
        api
            .get<ApiEnvelope<BankAccount[]>>('/settings/bank-accounts')
            .then((r) => r.data.data),

    createBankAccount: (body: CreateBankAccountInput) =>
        api
            .post<ApiEnvelope<BankAccount>>('/settings/bank-accounts', body)
            .then((r) => r.data.data),

    updateBankAccount: (id: string, body: UpdateBankAccountInput) =>
        api
            .patch<ApiEnvelope<BankAccount>>(`/settings/bank-accounts/${id}`, body)
            .then((r) => r.data.data),

    deleteBankAccount: (id: string) =>
        api
            .delete<ApiEnvelope<{ deleted: boolean }>>(`/settings/bank-accounts/${id}`)
            .then((r) => r.data.data),

    setDefaultBankAccount: (id: string) =>
        api
            .post<ApiEnvelope<BankAccount>>(`/settings/bank-accounts/${id}/set-default`)
            .then((r) => r.data.data),

    // ===== E-Wallets =====
    listEWallets: () =>
        api
            .get<ApiEnvelope<EWallet[]>>('/settings/e-wallets')
            .then((r) => r.data.data),

    createEWallet: (body: CreateEWalletInput) =>
        api
            .post<ApiEnvelope<EWallet>>('/settings/e-wallets', body)
            .then((r) => r.data.data),

    updateEWallet: (id: string, body: UpdateEWalletInput) =>
        api
            .patch<ApiEnvelope<EWallet>>(`/settings/e-wallets/${id}`, body)
            .then((r) => r.data.data),

    deleteEWallet: (id: string) =>
        api
            .delete<ApiEnvelope<{ deleted: boolean }>>(`/settings/e-wallets/${id}`)
            .then((r) => r.data.data),

    setDefaultEWallet: (id: string) =>
        api
            .post<ApiEnvelope<EWallet>>(`/settings/e-wallets/${id}/set-default`)
            .then((r) => r.data.data),

    // ===== Card Terminals =====
    listCardTerminals: () =>
        api
            .get<ApiEnvelope<CardTerminal[]>>('/settings/card-terminals')
            .then((r) => r.data.data),

    createCardTerminal: (body: CreateCardTerminalInput) =>
        api
            .post<ApiEnvelope<CardTerminal>>('/settings/card-terminals', body)
            .then((r) => r.data.data),

    updateCardTerminal: (id: string, body: UpdateCardTerminalInput) =>
        api
            .patch<ApiEnvelope<CardTerminal>>(`/settings/card-terminals/${id}`, body)
            .then((r) => r.data.data),

    deleteCardTerminal: (id: string) =>
        api
            .delete<ApiEnvelope<{ deleted: boolean }>>(`/settings/card-terminals/${id}`)
            .then((r) => r.data.data),

    setDefaultCardTerminal: (id: string) =>
        api
            .post<ApiEnvelope<CardTerminal>>(
                `/settings/card-terminals/${id}/set-default`,
            )
            .then((r) => r.data.data),

    // ===== Phase 3 — E-Invoice =====
    getEInvoice: () =>
        api.get<ApiEnvelope<EInvoiceConfig>>('/settings/einvoice').then((r) => r.data.data),

    updateEInvoice: (body: EInvoiceUpdateInput) =>
        api
            .patch<ApiEnvelope<EInvoiceConfig>>('/settings/einvoice', body)
            .then((r) => r.data.data),

    // ===== Phase 3 — Notifications =====
    getNotifications: () =>
        api
            .get<ApiEnvelope<NotificationsConfig>>('/settings/notifications')
            .then((r) => r.data.data),

    updateNotifications: (body: NotificationsUpdateInput) =>
        api
            .patch<ApiEnvelope<NotificationsConfig>>('/settings/notifications', body)
            .then((r) => r.data.data),

    // ===== Cấu hình chung =====
    getGeneral: () =>
        api
            .get<ApiEnvelope<GeneralConfig>>('/settings/general')
            .then((r) => r.data.data),

    updateGeneral: (body: GeneralUpdateInput) =>
        api
            .patch<ApiEnvelope<GeneralConfig>>('/settings/general', body)
            .then((r) => r.data.data),

    // ===== Integrations — GHTK + Napas =====
    getIntegrations: () =>
        api
            .get<ApiEnvelope<IntegrationsConfig>>('/settings/integrations')
            .then((r) => r.data.data),

    updateIntegrations: (body: IntegrationsUpdateInput) =>
        api
            .patch<ApiEnvelope<IntegrationsConfig>>('/settings/integrations', body)
            .then((r) => r.data.data),

    testBestConnection: (body: BestConnectionInput) =>
        api
            .post<ApiEnvelope<BestConnectionResult>>(
                '/settings/integrations/shipping/best/test-connection',
                body,
            )
            .then((r) => r.data.data),

    /** Tạo token mới — vô hiệu token cũ ngay lập tức. */
    generateBestWebhookToken: (body: BestWebhookTokenInput) =>
        api
            .post<ApiEnvelope<BestWebhookTokenResult>>(
                '/settings/integrations/shipping/best/webhook-token',
                body,
            )
            .then((r) => r.data.data),

    /**
     * Xem lại token đã sinh. Dùng POST (không phải GET) để token không lọt vào
     * access log qua URL và không bị cache.
     */
    revealBestWebhookToken: (body: BestWebhookTokenInput) =>
        api
            .post<ApiEnvelope<BestWebhookTokenResult>>(
                '/settings/integrations/shipping/best/webhook-token/reveal',
                body,
            )
            .then((r) => r.data.data),

    // ===== Phase 4 — API Keys =====
    listApiKeys: () =>
        api.get<ApiEnvelope<ApiKey[]>>('/settings/api-keys').then((r) => r.data.data),

    createApiKey: (body: CreateApiKeyInput) =>
        api
            .post<ApiEnvelope<CreateApiKeyResponse>>('/settings/api-keys', body)
            .then((r) => r.data.data),

    revokeApiKey: (id: string) =>
        api
            .delete<ApiEnvelope<ApiKey>>(`/settings/api-keys/${id}`)
            .then((r) => r.data.data),

    // ===== Phase 4 — Audit Log =====
    listAuditLog: (q: AuditLogQueryInput = {}) =>
        api
            .get<AuditLogResponse>('/settings/audit-log', { params: q })
            .then((r) => r.data),

    // ===== Đồng bộ tìm kiếm (Elasticsearch) =====
    /**
     * POST /v1/settings/search/sync-products
     * Đẩy toàn bộ sản phẩm ACTIVE của tenant vào hàng đợi index. Chỉ phạm vi
     * tenant hiện tại — không dựng lại index dùng chung của mọi tenant.
     */
    syncProductsToSearch: () =>
        api
            .post<ApiEnvelope<ProductSearchSyncResult>>(
                '/settings/search/sync-products',
                undefined,
                {
                    // BE quét cursor rồi đẩy từng job vào BullMQ, thời gian tăng
                    // tuyến tính theo số sản phẩm — timeout mặc định 30s của
                    // client cắt ngang cửa hàng vài nghìn sản phẩm.
                    timeout: PRODUCT_SEARCH_SYNC_TIMEOUT_MS,
                    // Lượt sync chậm/đứt KHÔNG được coi là "backend chết": cơ chế
                    // markApiUnavailable sẽ chặn mọi request khác của cả ứng dụng.
                    skipApiAvailabilityTracking: true,
                },
            )
            .then((r) => r.data.data),
};
